import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, of, switchMap, withLatestFrom} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {getProductId} from '../../../../../models/clare';
import {AnalyticsEvents, AnalyticsService} from '../../../../../services/analytics.service';
import {CatalogApiService} from '../../../../../services/catalog-api.service';
import {CreateProductService} from '../../../../../services/product/create-product-service';
import {selectSelectedOrganizationId} from '../../../../../state/router/router.selectors';
import {CreateProductActions} from './create-product.actions';

const FRIENDLY_CREATE_PRODUCT_ERRORS = new Set([
  'Organization context could not be loaded. Please refresh and try again.',
]);

@Injectable()
export class CreateProductEffects {
  loadCatalogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateProductActions.loadCatalogs),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([, organizationId]) => {
        if (!organizationId) {
          return of(CreateProductActions.loadCatalogsFailure());
        }

        return this.catalogApi.getAllCatalogsByOrganizationId(organizationId).pipe(
          map(catalogs => CreateProductActions.loadCatalogsSuccess({catalogs})),
          catchError(() => of(CreateProductActions.loadCatalogsFailure()))
        );
      })
    )
  );

  createProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateProductActions.createProduct),
      switchMap(({data, requestId}) => {
        return this.createProductService.save(data).pipe(
          tap(product => {
            this.analytics.trackAction(AnalyticsEvents.AddProduct, {
              product_id: String(getProductId(product)),
              event_type: 1,
            });
          }),
          map(product => CreateProductActions.createProductSuccess({product, requestId})),
          catchError(error => {
            const message = FRIENDLY_CREATE_PRODUCT_ERRORS.has(error?.message)
              ? error.message
              : 'An error occurred, please refresh and try again.';
            return of(
              CreateProductActions.createProductFailure({
                error: {message},
                requestId,
              })
            );
          })
        );
      })
    )
  );

  createProductSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateProductActions.createProductSuccess),
        tap(() => {
          this.toast.done('Successfully created product');
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private toast: SeFeToastService,
    private catalogApi: CatalogApiService,
    private createProductService: CreateProductService,
    private analytics: AnalyticsService
  ) {}
}
